/**
 * Owner: Masa Bayu
 * File: hub-auth.ts
 * Date:Time: 2026-10-17 23:55
 * Description: Fasa 4 — status auth qxp-hub untuk qxread: token sessions dalam
 *   localStorage, mesin status 'anonymous' | 'signed-in' | 'expired' yang reaktif
 *   melalui useSyncExternalStore (corak catalog-port). Token tidak pernah ditaip
 *   semula oleh UI — register/login menghantarnya terus ke sini daripada hub-sync.
 */

import { useSyncExternalStore } from 'react';

export type HubAuthState = 'anonymous' | 'signed-in' | 'expired';

const TOKEN_KEY = 'qxread.hub.token.v1';
const ACCOUNT_KEY = 'qxread.hub.account.v1';
/** §468u N5 — peranan ditulis HANYA daripada respons hub yang disahkan, supaya
 *   handoff ke qxeditor tidak mengagak peranan penerbit. */
const ROLE_KEY = 'qxread.hub.role.v1';

function readStoredToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const token = localStorage.getItem(TOKEN_KEY);
  return typeof token === 'string' && token.length > 0 ? token : null;
}

function readStoredAccountId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const accountId = localStorage.getItem(ACCOUNT_KEY);
  return typeof accountId === 'string' && accountId.length > 0 ? accountId : null;
}

let state: HubAuthState = readStoredToken() !== null ? 'signed-in' : 'anonymous';
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function getHubAuthState(): HubAuthState {
  return state;
}

export function subscribeHubAuth(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useHubAuthState(): HubAuthState {
  return useSyncExternalStore(subscribeHubAuth, getHubAuthState);
}

export function getHubAuthToken(): string | null {
  return state === 'signed-in' ? readStoredToken() : null;
}

export function getHubAuthAccountId(): string | null {
  return readStoredAccountId();
}

/** Simpan sesi baharu (register/login berjaya) dan umumkan peralihan ke 'signed-in'. */
export function setHubAuth(token: string, accountId: string): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(ACCOUNT_KEY, accountId);
  }
  state = 'signed-in';
  emit();
}

/**
 * Rekod peranan seperti yang diisytiharkan hub (`identity.role` daripada
 * `GET /v1/publisher/dashboard` 200). Tiada nilai rekaan: hanya dipanggil
 * selepas hub mengesahkan sesi.
 */
export function setHubAuthRole(role: string): void {
  if (typeof localStorage !== 'undefined' && role.length > 0) {
    localStorage.setItem(ROLE_KEY, role);
  }
}

/**
 * Buang sesi (log keluar / sesi ditolak server). §509 F4 — fungsi ini buang TOKEN
 * sahaja; laluan produksi wajib guna `endHubSession()` / `expireHubSession()`
 * (hub-sync) yang juga membuang grant hub + cache artifak. Hub-auth tidak boleh
 * import registry (registry → hub-auth untuk header sesi) — itu.cycle import.
 */
export function clearHubAuth(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ACCOUNT_KEY);
    localStorage.removeItem(ROLE_KEY);
  }
  state = 'anonymous';
  emit();
}

/**
 * Token ditolak server (401) — sesi tamat. Token dibuang supaya panggilan
 * seterusnya tidak menghantar token basi; status 'expired' memberi isyarat UI
 * untuk meminta daftar masuk semula (akaun kekal dikenali untuk paparan).
 */
export function markHubAuthExpired(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
  }
  if (state !== 'expired') {
    state = 'expired';
    emit();
  }
}

/** Header Authorization untuk panggilan hub yang memerlukan sesi. */
export function hubAuthHeaders(): Record<string, string> {
  const token = getHubAuthToken();
  return token === null ? {} : { authorization: `Bearer ${token}` };
}

/** Ujian sahaja — buang cache supaya setiap kes berdiri sendiri. */
export function __resetHubAuthForTests(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ACCOUNT_KEY);
    localStorage.removeItem(ROLE_KEY);
  }
  state = 'anonymous';
  listeners.clear();
}

/** Ujian sahaja — terapkan sesi tanpa panggilan rangkaian. */
export function __setHubAuthForTests(token: string, accountId: string): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(ACCOUNT_KEY, accountId);
  }
  state = 'signed-in';
}
